// "Who's free, and when would it be done?" inside the Work Orders add-operation
// forms, so a General Labor operation can be scheduled while it's being created
// instead of landing in the Scheduler backlog. Spring Commissioning and Winterizing
// are left alone — they're booked closer to their start date.
//
// The answer comes from schedulerEngine.js, the same engine the Scheduler board
// runs, so what's offered here is what the board will show once it's saved.

import { idbCursorAll, escapeHTML } from './utilities';
import { loadScheduleEnv, suggestTechs, projectWorkOrders, shortDate, getTodayString } from './schedulerEngine';

const ASSIST_CATEGORY = 'General Labor';
const ENV_TTL_MS = 60000;
const SHOWN_AT_FIRST = 4;

let envPromise = null;
let envAt = 0;

// Crew, time off and haul/launch days change rarely — reused for a minute so typing
// in the hours field doesn't refetch them on every keystroke.
export function scheduleEnv(force) {
  if (force || !envPromise || Date.now() - envAt > ENV_TTL_MS) {
    envAt = Date.now();
    envPromise = loadScheduleEnv().catch(err => { envPromise = null; throw err; });
  }
  return envPromise;
}

const dayName = ds => new Date(`${ds}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });
const fmtDay = ds => `${dayName(ds)} ${shortDate(ds)}`;

/**
 * Mounts the picker into `box`.
 *   getDraft() -> { category, hours, begin, opType, description, shortDesc }
 *   categories: the categories it offers a tech for (equipment work is Internal).
 * Returns { refresh(), selection(), reset() }; selection() is
 * { tech, start, end } once a tech is picked, otherwise null.
 */
export function mountScheduleAssist({ box, getDraft, categories = [ASSIST_CATEGORY] }) {
  let picked = null;      // tech name
  let result = null;      // last suggestTechs() output
  let showAll = false;
  let seq = 0;
  let timer = null;

  function draw(message) {
    const draft = getDraft();
    if (!categories.includes(draft.category)) {
      box.classList.add('hidden');
      box.innerHTML = '';
      return;
    }
    box.classList.remove('hidden');

    if (message) {
      box.innerHTML = `<div class="sa-head"><span class="sa-title">Schedule it now</span></div><div class="sa-note">${escapeHTML(message)}</div>`;
      return;
    }

    const rows = result.rows;
    const best = rows.find(r => r.end);
    const visible = showAll ? rows : rows.filter((r, i) => i < SHOWN_AT_FIRST || r.tech.name === picked);
    const pickedRow = picked ? rows.find(r => r.tech.name === picked) : null;
    const headline = pickedRow && pickedRow.end
      ? `Projected completion <b>${fmtDay(pickedRow.end)}</b> with ${escapeHTML(pickedRow.tech.name)}`
      : best ? `Earliest completion <b>${fmtDay(best.end)}</b> with ${escapeHTML(best.tech.name)}` : 'No open hours found';

    box.innerHTML = `
      <div class="sa-head">
        <span class="sa-title">Schedule it now</span>
        <span class="sa-headline">${headline}</span>
      </div>
      <div class="sa-list">
        <div class="sa-row${picked ? '' : ' selected'}" data-sa-pick="">
          <span class="sa-radio"></span>
          <span class="sa-name">Leave unscheduled</span>
          <span class="sa-when">Goes to the Scheduler backlog</span>
        </div>
        ${visible.map(r => `
          <div class="sa-row${r.tech.name === picked ? ' selected' : ''}${r.end ? '' : ' is-full'}" ${r.end ? `data-sa-pick="${escapeHTML(r.tech.name)}"` : ''}>
            <span class="sa-radio"></span>
            <span class="sa-name">${escapeHTML(r.tech.fullName || r.tech.name)}${r.match ? '<span class="sa-skill">skill ✓</span>' : ''}</span>
            <span class="sa-when">${r.end
              ? (r.start === r.end ? `${fmtDay(r.start)}` : `Starts ${fmtDay(r.start)} · done <b>${fmtDay(r.end)}</b>`)
              : 'No open hours'}</span>
          </div>`).join('')}
      </div>
      <div class="sa-foot">
        ${rows.length > visible.length ? `<span class="sa-more" data-sa-more="1">Show all ${rows.length} techs</span>` : ''}
        <span class="sa-hint">${result.skill ? `Techs rated High in ${escapeHTML(result.skill)} first. ` : ''}Uses open hours only, so nothing already scheduled moves.</span>
      </div>`;
  }

  async function refresh() {
    const draft = getDraft();
    if (!categories.includes(draft.category)) { picked = null; result = null; draw(); return; }
    const hours = parseFloat(draft.hours) || 0;
    if (!(hours > 0)) { picked = null; result = null; draw('Enter estimated hours to see who is free and a projected completion date.'); return; }
    const mine = ++seq;
    try {
      const [env, jobs] = await Promise.all([scheduleEnv(), idbCursorAll('jobData')]);
      if (mine !== seq) return; // a newer keystroke is already being worked out
      if (!env.techs.length) { picked = null; result = null; draw('No techs on the crew list yet (Settings > Team).'); return; }
      result = suggestTechs(env, jobs, {
        Estimated_Hours: hours,
        Op_Type: draft.opType || '',
        Description: draft.description || '',
        Short_Description: draft.shortDesc || '',
      }, draft.begin || '');
      if (picked && !result.rows.some(r => r.tech.name === picked && r.end)) picked = null;
      draw();
    } catch (err) {
      console.error('[scheduleAssist] could not work out availability', err);
      if (mine === seq) { picked = null; result = null; draw('Could not load the schedule. The operation can still be added unscheduled.'); }
    }
  }

  box.addEventListener('click', (e) => {
    if (e.target.closest('[data-sa-more]')) { showAll = true; draw(); return; }
    const row = e.target.closest('[data-sa-pick]');
    if (!row || !result) return;
    picked = row.getAttribute('data-sa-pick') || null;
    draw();
  });

  return {
    refresh,
    // Debounced, for input listeners.
    refreshSoon() { clearTimeout(timer); timer = setTimeout(refresh, 250); },
    selection() {
      if (!picked || !result || !categories.includes(getDraft().category)) return null;
      const row = result.rows.find(r => r.tech.name === picked);
      return row && row.end ? { tech: row.tech.name, start: row.start, end: row.end } : null;
    },
    reset() { picked = null; result = null; showAll = false; seq++; box.innerHTML = ''; box.classList.add('hidden'); },
  };
}

// ---------- projected finish per work order (Work Orders rows) ----------

let projection = new Map();
let projectionTimer = null;
const projectionListeners = new Set();

/** { end: 'YYYY-MM-DD', late } for a work order whose open operations all have a tech, else undefined. */
export function workOrderProjection(workOrder) {
  return projection.get(String(workOrder));
}

export function onProjectionChange(fn) {
  projectionListeners.add(fn);
}

/** Recomputes every work order's projected finish shortly (coalesces bursts of job changes). */
export function refreshProjectionSoon(delay = 1500) {
  clearTimeout(projectionTimer);
  projectionTimer = setTimeout(async () => {
    try {
      const [env, jobs] = await Promise.all([scheduleEnv(), idbCursorAll('jobData')]);
      projection = projectWorkOrders(env, jobs);
      projectionListeners.forEach(fn => fn());
    } catch (err) {
      console.error('[scheduleAssist] could not project work order finish dates', err);
    }
  }, delay);
}

/** "Est. done Oct 9" text for a work order row, or '' when there's nothing to say. */
export function projectionLabel(workOrder) {
  const p = workOrderProjection(workOrder);
  if (!p || !p.end) return '';
  const today = getTodayString();
  const when = p.end === today ? 'today' : shortDate(p.end);
  return `Est. done ${when}${p.late ? ' · running late' : ''}`;
}
